import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaPlay, FaPause, FaRedo, FaSave, FaTrash, FaPlus, FaChartLine, FaArrowLeft, FaVolumeUp } from 'react-icons/fa';
import './Breathwork.css';
import './DesktopComponentStyles.css';
import logger from '../utils/logger';
import GuideModal from './GuideModal';

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'https://backend-rough-bird-115.fly.dev';

// Bell sound URL - using a publicly available sound
const BELL_SOUND_URL = 'https://soundbible.com/grab.php?id=1496&type=mp3'; // Tibetan Bell sound
const TIMER_SOUND_URL = 'https://soundbible.com/grab.php?id=2156&type=mp3'; // Timer sound

// Voice instruction URLs with actual voice instructions
const VOICE_INSTRUCTIONS = {
  inhale: '/audio/inhale.mp3',
  hold: '/audio/hold.mp3',
  exhale: '/audio/exhale.mp3'
};

// Simple notification component
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 
                 type === 'error' ? 'bg-red-500' : 
                 type === 'info' ? 'bg-blue-500' : 'bg-gray-500';

  return (
    <div className={`fixed bottom-4 right-4 ${bgColor} text-white py-2 px-4 rounded-md shadow-lg z-50`}>
      {message}
    </div>
  );
};

const Breathwork = () => {
  // States
  const [presets, setPresets] = useState([]);
  const [customPresets, setCustomPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('ready'); // ready, inhale, hold1, exhale, hold2
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(0);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [totalCycles, setTotalCycles] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalTime: 0,
    completedSessions: 0,
    recentTotalTime: 0,
    recentCompletedSessions: 0,
    recentSessions: 0
  });
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [bellVolume, setBellVolume] = useState(() => {
    const savedVolume = localStorage.getItem('breathworkBellVolume');
    return savedVolume ? parseFloat(savedVolume) : 0.7; // Default to 70% volume
  });
  
  // Notification state
  const [notification, setNotification] = useState(null);
  
  // Custom preset state
  const [customPreset, setCustomPreset] = useState({
    name: '',
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
    cycles: 5
  });

  // References
  const timerRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const audioRef = useRef(null);
  const bellSoundRef = useRef(null);
  const voiceInstructionsRef = useRef({
    inhale: null,
    hold: null,
    exhale: null
  });
  const circleRef = useRef(null);
  const lastPhaseTimeRef = useRef(null);

  // Initialize sounds
  useEffect(() => {
    // Initialize bell sound for session completion
    bellSoundRef.current = new Audio(BELL_SOUND_URL);
    bellSoundRef.current.volume = bellVolume;
    
    // Initialize voice instruction audio elements
    voiceInstructionsRef.current = {
      inhale: new Audio(VOICE_INSTRUCTIONS.inhale),
      hold: new Audio(VOICE_INSTRUCTIONS.hold),
      exhale: new Audio(VOICE_INSTRUCTIONS.exhale)
    };
    
    // Set volume for all voice instructions
    Object.values(voiceInstructionsRef.current).forEach(audio => {
      if (audio) {
        audio.volume = bellVolume;
      }
    });
    
    // Make sure audioRef is also initialized
    if (audioRef.current) {
      audioRef.current.src = TIMER_SOUND_URL;
      audioRef.current.load(); // Force loading the audio
    }
    
    return () => {
      // Clean up audio elements
      if (bellSoundRef.current) {
        bellSoundRef.current.pause();
        bellSoundRef.current = null;
      }
      
      // Clean up voice instruction audio elements
      Object.values(voiceInstructionsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
        }
      });
      voiceInstructionsRef.current = null;
    };
  }, []);

  // Update sound volumes when it changes
  useEffect(() => {
    if (bellSoundRef.current) {
      bellSoundRef.current.volume = bellVolume;
    }
    
    // Update volume for voice instructions
    Object.values(voiceInstructionsRef.current || {}).forEach(audio => {
      if (audio) {
        audio.volume = bellVolume;
      }
    });
    
    localStorage.setItem('breathworkBellVolume', bellVolume.toString());
  }, [bellVolume]);

  // Custom notification function
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  // Define fetchBreathworkData outside useEffect so it can be used elsewhere
  const fetchBreathworkData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/breathwork`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPresets(response.data.presets || []);
      setCustomPresets(response.data.customPresets || []);
      
      // Set default preset if available
      if (response.data.lastUsed) {
        setSelectedPreset(response.data.lastUsed);
      } else if (response.data.presets && response.data.presets.length > 0) {
        setSelectedPreset(response.data.presets[0]);
      }
      
    } catch (error) {
      logger.error('Error fetching breathwork data:', error);
      showNotification('Failed to load breathwork data', 'error');
    }
  };

  // Fetch preset data on component mount
  useEffect(() => {
    fetchBreathworkData();
  }, []);

  // Determine the next phase in the cycle
  const getNextPhase = useCallback((currentPhase, preset) => {
    const { inhale, hold1, exhale, hold2 } = preset;
    
    switch (currentPhase) {
      case 'ready':
        return { phase: 'inhale', duration: inhale };
      case 'inhale':
        return hold1 > 0 
          ? { phase: 'hold1', duration: hold1 } 
          : { phase: 'exhale', duration: exhale };
      case 'hold1':
        return { phase: 'exhale', duration: exhale };
      case 'exhale':
        return hold2 > 0 
          ? { phase: 'hold2', duration: hold2 } 
          : { phase: 'inhale', duration: inhale, newCycle: true };
      case 'hold2':
        return { phase: 'inhale', duration: inhale, newCycle: true };
      default:
        return { phase: 'ready', duration: 0 };
    }
  }, []);

  // Play voice instruction when phase changes
  const playPhaseChangeSound = useCallback((phase) => {
    // Determine which voice instruction to play based on the phase
    let instructionAudio;
    
    switch (phase) {
      case 'inhale':
        instructionAudio = voiceInstructionsRef.current?.inhale;
        break;
      case 'hold1':
      case 'hold2':
        instructionAudio = voiceInstructionsRef.current?.hold;
        break;
      case 'exhale':
        instructionAudio = voiceInstructionsRef.current?.exhale;
        break;
      default:
        // No instruction for 'ready' or unknown phases
        return;
    }
    
    if (instructionAudio) {
      try {
        // Reset the audio to the beginning
        instructionAudio.currentTime = 0;
        
        const playPromise = instructionAudio.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            logger.debug('Voice instruction play error:', e);
            // If autoplay is blocked, we can show a message to the user
            showNotification('Audio playback was blocked. Please interact with the page first.', 'info');
          });
        }
      } catch (e) {
        logger.debug('Voice instruction play error:', e);
      }
    }
  }, []);

  // Play bell sound at the end of session
  const playBellSound = useCallback(() => {
    if (bellSoundRef.current) {
      // Reset the audio to the beginning in case it was already played
      bellSoundRef.current.currentTime = 0;
      bellSoundRef.current.play().catch(error => {
        logger.error('Error playing bell sound:', error);
      });
    }
  }, []);

  // Move to the next phase in the breathing cycle
  const moveToNextPhase = useCallback(() => {
    if (!selectedPreset) return;
    
    // Determine the next phase
    const nextPhaseInfo = getNextPhase(currentPhase, selectedPreset);
    
    // Play voice instruction for the new phase
    playPhaseChangeSound(nextPhaseInfo.phase);
    
    // Update cycle count if needed
    if (nextPhaseInfo.newCycle) {
      setCurrentCycle(prev => {
        const newCycleCount = prev + 1;
        // Check if we've completed all cycles
        if (newCycleCount > totalCycles) {
          completeSession();
          return prev;
        }
        return newCycleCount;
      });
    }
    
    // Update the phase
    setCurrentPhase(nextPhaseInfo.phase);
    setPhaseTimeLeft(nextPhaseInfo.duration);
    lastPhaseTimeRef.current = Date.now();
    
  }, [currentPhase, selectedPreset, totalCycles, getNextPhase, playPhaseChangeSound]);

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setBellVolume(newVolume);
  };

  // Run the timer when isRunning is true
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        // Update elapsed time
        if (sessionStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000);
          setElapsedTime(elapsed);
        }
        
        // Update phase timer
        setPhaseTimeLeft(prev => {
          const timePassed = Math.floor((Date.now() - lastPhaseTimeRef.current) / 1000);
          const timeLeft = Math.max(0, (selectedPreset ? 
            (currentPhase === 'inhale' ? selectedPreset.inhale :
             currentPhase === 'hold1' ? selectedPreset.hold1 :
             currentPhase === 'exhale' ? selectedPreset.exhale :
             currentPhase === 'hold2' ? selectedPreset.hold2 : 0) 
            : 0) - timePassed);
          
          // If time is up, move to next phase
          if (timeLeft === 0 && prev > 0) {
            moveToNextPhase();
          }
          return timeLeft;
        });
      }, 100); // Update more frequently for smoother countdown
    } else {
      clearInterval(timerRef.current);
    }
    
    return () => clearInterval(timerRef.current);
  }, [isRunning, currentPhase, selectedPreset, moveToNextPhase]);

  // Update circle animation when phase changes
  useEffect(() => {
    if (!circleRef.current || !selectedPreset || currentPhase === 'ready') return;
    
    const circle = circleRef.current;
    
    // Reset animation
    circle.style.animation = 'none';
    
    // Force a reflow
    const reflow = circle.offsetHeight;
    
    let animationDuration;
    let animationName;
    
    switch (currentPhase) {
      case 'inhale':
        animationDuration = `${selectedPreset.inhale}s`;
        animationName = 'circle-expand';
        break;
      case 'hold1':
        animationDuration = `${selectedPreset.hold1}s`;
        animationName = 'circle-pulsate';
        break;
      case 'exhale':
        animationDuration = `${selectedPreset.exhale}s`;
        animationName = 'circle-contract';
        break;
      case 'hold2':
        animationDuration = `${selectedPreset.hold2}s`;
        animationName = 'circle-pulsate';
        break;
      default:
        return;
    }
    
    circle.style.animation = `${animationName} ${animationDuration} linear forwards`;
  }, [currentPhase, selectedPreset]);

  const startSession = () => {
    if (!selectedPreset) return;
    
    // Save initial settings
    setCurrentCycle(1);
    setTotalCycles(selectedPreset.cycles);
    setCurrentPhase('inhale');
    setPhaseTimeLeft(selectedPreset.inhale);
    setIsRunning(true);
    setIsComplete(false);
    
    // Start timers
    sessionStartTimeRef.current = Date.now();
    lastPhaseTimeRef.current = Date.now();
    setElapsedTime(0);
  };

  const pauseSession = () => {
    setIsRunning(false);
  };

  const resumeSession = () => {
    // Update the last phase time when resuming
    lastPhaseTimeRef.current = Date.now() - ((selectedPreset ? 
      (currentPhase === 'inhale' ? selectedPreset.inhale :
       currentPhase === 'hold1' ? selectedPreset.hold1 :
       currentPhase === 'exhale' ? selectedPreset.exhale :
       currentPhase === 'hold2' ? selectedPreset.hold2 : 0) 
      : 0) - phaseTimeLeft) * 1000;
    
    setIsRunning(true);
  };

  const resetSession = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setIsComplete(false);
    setCurrentPhase('ready');
    setPhaseTimeLeft(0);
    setCurrentCycle(1);
    setTotalCycles(0);
    setElapsedTime(0);
    sessionStartTimeRef.current = null;
    lastPhaseTimeRef.current = null;
  };

  const saveSession = async (completed = false, duration = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('You must be logged in to save sessions', 'error');
        return;
      }

      // Calculate session duration if not provided
      const sessionDuration = duration || elapsedTime;
      
      // Prepare session data
      const sessionData = {
        preset: selectedPreset,
        completed,
        duration: sessionDuration,
        date: new Date().toISOString()
      };

      // Save to API
      const response = await axios.post(`${API_URL}/breathwork/session`, sessionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mark first breath cycle task as completed if this is a completed session
      if (completed && currentCycle > 1) {
        markBreathCycleTaskCompleted();
      }

      showNotification('Session saved successfully!', 'success');
      
      // Refresh history data
      fetchHistoryData();
      
      return response.data;
    } catch (error) {
      logger.error('Error saving session:', error);
      showNotification('Failed to save session', 'error');
    }
  };

  // Function to mark breath cycle task as completed
  const markBreathCycleTaskCompleted = () => {
    // Set localStorage flag
    localStorage.setItem('firstBreathCycle', 'true');
    
    // Update the server if possible
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // API_URL already includes '/api' in production, so we don't need to add it again
        axios.post(`${API_URL}/users/starter-task`, 
          { task: 'firstBreathCycle', completed: true },
          { headers: { Authorization: `Bearer ${token}` }}
        ).then(response => {
          logger.debug('Breath cycle completion status updated on server:', response.data);
        }).catch(error => {
          logger.error('Error updating breath cycle completion status on server:', error);
        });
      } catch (error) {
        logger.error('Error updating breath cycle completion status:', error);
      }
    }
  };

  const fetchHistoryData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/breathwork/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHistory(response.data.history || []);
      setStats(response.data.stats || {
        totalSessions: 0,
        totalTime: 0,
        completedSessions: 0,
        recentTotalTime: 0,
        recentCompletedSessions: 0,
        recentSessions: 0
      });
      
    } catch (error) {
      logger.error('Error fetching breathwork history:', error);
      showNotification('Failed to load history data', 'error');
    }
  };

  const saveCustomPresetHandler = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Validate inputs
      if (!customPreset.name.trim()) {
        showNotification('Please provide a name for your preset', 'error');
        return;
      }
      
      if (customPreset.inhale <= 0 || customPreset.exhale <= 0 || 
          customPreset.cycles <= 0 || customPreset.hold1 < 0 || customPreset.hold2 < 0) {
        showNotification('Please ensure all values are valid', 'error');
        return;
      }
      
      await axios.post(
        `${API_URL}/breathwork/preset`,
        customPreset,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showNotification('Custom preset saved!', 'success');
      
      // Refresh presets
      fetchBreathworkData();
      setShowCustomForm(false);
      
    } catch (error) {
      logger.error('Error saving custom preset:', error);
      showNotification('Failed to save custom preset', 'error');
    }
  };

  const deleteCustomPreset = async (name) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await axios.delete(`${API_URL}/breathwork/preset/${name}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showNotification('Custom preset deleted', 'success');
      
      // Refresh presets
      fetchBreathworkData();
      
    } catch (error) {
      logger.error('Error deleting custom preset:', error);
      showNotification('Failed to delete preset', 'error');
    }
  };

  const formatSessionTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleHistory = () => {
    if (!showHistory) {
      fetchHistoryData();
    }
    setShowHistory(!showHistory);
  };

  const progressPercentage = () => {
    if (!totalCycles || totalCycles === 0) return 0;
    return Math.min(100, ((currentCycle - 1) / totalCycles) * 100);
  };

  // Complete the session
  const completeSession = useCallback(() => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setIsComplete(true);
    setCurrentPhase('ready');
    
    // Play bell sound at completion
    playBellSound();
    
    // Save the session
    const duration = elapsedTime;
    saveSession(true, duration);
  }, [elapsedTime, playBellSound]);

  // Render functions
  const renderPresetSelector = () => (
    <div className="mt-6 space-y-4">
      <h3 className="text-lg font-medium text-gray-900 mb-2">Select Breathing Technique</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {presets.map((preset, index) => (
          <button
            key={`preset-${index}`}
            className={`p-4 rounded-lg border-2 transition-all 
              ${selectedPreset?.name === preset.name 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-blue-300'}`}
            onClick={() => setSelectedPreset(preset)}
          >
            <h4 className="font-medium text-gray-900">{preset.name}</h4>
            <div className="text-sm text-gray-500 mt-1">
              {preset.inhale}s in • {preset.hold1}s hold • {preset.exhale}s out • {preset.hold2}s hold
            </div>
            <div className="text-sm text-gray-600 mt-1">{preset.cycles} cycles</div>
          </button>
        ))}
        
        {customPresets.map((preset, index) => (
          <button
            key={`custom-${index}`}
            className={`relative p-4 rounded-lg border-2 transition-all 
              ${selectedPreset?.name === preset.name 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-blue-300'}`}
            onClick={() => setSelectedPreset(preset)}
          >
            <div className="absolute top-2 right-2">
              <button 
                className="text-gray-400 hover:text-red-500 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteCustomPreset(preset.name);
                }}
              >
                <FaTrash size={14} />
              </button>
            </div>
            <h4 className="font-medium text-gray-900">{preset.name}</h4>
            <div className="text-sm text-gray-500 mt-1">
              {preset.inhale}s in • {preset.hold1}s hold • {preset.exhale}s out • {preset.hold2}s hold
            </div>
            <div className="text-sm text-gray-600 mt-1">{preset.cycles} cycles</div>
          </button>
        ))}
        
        <button
          className="p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-300 transition-all flex flex-col items-center justify-center text-gray-500 hover:text-blue-500"
          onClick={() => setShowCustomForm(true)}
        >
          <FaPlus size={20} className="mb-2" />
          <span>Create Custom</span>
        </button>
      </div>
    </div>
  );

  const renderBreathingCircle = () => (
    <div className="flex flex-col items-center justify-center mt-8 mb-10">
      <div className="relative w-64 h-64 sm:w-80 sm:h-80">
        {/* Background circle */}
        <div className="absolute inset-0 rounded-full border-8 border-gray-100"></div>
        
        {/* Progress circle */}
        <div 
          ref={circleRef} 
          className="absolute inset-0 rounded-full border-8 border-blue-500 transition-all"
          style={{
            width: isRunning || isComplete ? '100%' : '20%',
            height: isRunning || isComplete ? '100%' : '20%',
            top: isRunning || isComplete ? '0%' : '40%',
            left: isRunning || isComplete ? '0%' : '40%',
            transform: currentPhase === 'exhale' ? 'scale(0.5)' : 'scale(1)'
          }}
        ></div>
        
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          {isComplete ? (
            <div className="flex flex-col items-center">
              <div className="text-green-500 text-xl font-bold mb-1">Complete!</div>
              <div className="text-gray-700">
                {formatSessionTime(elapsedTime)} • {totalCycles} cycles
              </div>
            </div>
          ) : currentPhase === 'ready' ? (
            <div className="text-gray-700 text-lg font-semibold">Ready</div>
          ) : (
            <>
              <div className="text-blue-600 text-2xl font-bold mb-1">
                {currentPhase === 'inhale' ? 'Inhale' :
                 currentPhase === 'hold1' ? 'Hold' :
                 currentPhase === 'exhale' ? 'Exhale' : 'Hold'}
              </div>
              <div className="text-gray-700 text-3xl font-bold">{phaseTimeLeft}</div>
              {totalCycles > 0 && (
                <div className="text-gray-500 mt-2">
                  Cycle {currentCycle} of {totalCycles}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderSoundSettings = () => (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Voice & Sound Settings</h3>
        <button 
          className="text-gray-400 hover:text-gray-600"
          onClick={() => setShowSoundSettings(false)}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-2">
          Volume: {Math.round(bellVolume * 100)}%
        </label>
        <div className="flex items-center">
          <span className="mr-2 text-gray-500">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={bellVolume}
            onChange={handleVolumeChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="ml-2 text-gray-500">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </div>
      </div>
      
      <div className="mt-4">
        <p className="text-sm text-gray-600">
          Voice instructions will play at the beginning of each breath phase to guide you through the exercise.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button 
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200"
            onClick={() => {
              if (voiceInstructionsRef.current?.inhale) {
                voiceInstructionsRef.current.inhale.currentTime = 0;
                voiceInstructionsRef.current.inhale.play().catch(e => logger.debug('Test play error:', e));
              }
            }}
          >
            Test "Inhale"
          </button>
          <button 
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200"
            onClick={() => {
              if (voiceInstructionsRef.current?.hold) {
                voiceInstructionsRef.current.hold.currentTime = 0;
                voiceInstructionsRef.current.hold.play().catch(e => logger.debug('Test play error:', e));
              }
            }}
          >
            Test "Hold"
          </button>
          <button 
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200"
            onClick={() => {
              if (voiceInstructionsRef.current?.exhale) {
                voiceInstructionsRef.current.exhale.currentTime = 0;
                voiceInstructionsRef.current.exhale.play().catch(e => logger.debug('Test play error:', e));
              }
            }}
          >
            Test "Exhale"
          </button>
          <button 
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200"
            onClick={playBellSound}
          >
            Test Completion Bell
          </button>
        </div>
      </div>
    </div>
  );

  const renderControls = () => {
    if (isComplete) {
      return (
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <button
            className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition flex items-center justify-center"
            onClick={resetSession}
          >
            <FaRedo className="mr-2" /> New Session
          </button>
          <button
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 transition flex items-center justify-center"
            onClick={() => setShowSoundSettings(!showSoundSettings)}
          >
            <FaVolumeUp className="mr-2" /> Voice Settings
          </button>
        </div>
      );
    }
    
    if (currentPhase === 'ready') {
      return (
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          {selectedPreset && (
            <button
              className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition flex items-center justify-center"
              onClick={startSession}
            >
              <FaPlay className="mr-2" /> Start Session
            </button>
          )}
          <button
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 transition flex items-center justify-center"
            onClick={() => setShowSoundSettings(!showSoundSettings)}
          >
            <FaVolumeUp className="mr-2" /> Voice Settings
          </button>
        </div>
      );
    }
    
    return (
      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
        <button
          className="flex-1 py-3 px-4 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition flex items-center justify-center"
          onClick={resetSession}
        >
          <FaRedo className="mr-2" /> Reset
        </button>
        <button
          className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition flex items-center justify-center"
          onClick={isRunning ? pauseSession : resumeSession}
        >
          {isRunning ? <><FaPause className="mr-2" /> Pause</> : <><FaPlay className="mr-2" /> Resume</>}
        </button>
      </div>
    );
  };

  const renderProgressBar = () => (
    <div className="w-full h-2 bg-gray-200 rounded-full mt-2 mb-4">
      <div 
        className="h-full bg-blue-500 rounded-full"
        style={{ width: `${progressPercentage()}%` }}
      ></div>
    </div>
  );

  const renderCustomPresetForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold mb-4">Create Custom Preset</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 mb-1">Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={customPreset.name}
              onChange={(e) => setCustomPreset({...customPreset, name: e.target.value})}
              placeholder="My Custom Preset"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">Inhale (seconds)</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={customPreset.inhale}
                onChange={(e) => setCustomPreset({...customPreset, inhale: Number(e.target.value)})}
                min="1"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Hold After Inhale</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={customPreset.hold1}
                onChange={(e) => setCustomPreset({...customPreset, hold1: Number(e.target.value)})}
                min="0"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Exhale (seconds)</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={customPreset.exhale}
                onChange={(e) => setCustomPreset({...customPreset, exhale: Number(e.target.value)})}
                min="1"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Hold After Exhale</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={customPreset.hold2}
                onChange={(e) => setCustomPreset({...customPreset, hold2: Number(e.target.value)})}
                min="0"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Cycles</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={customPreset.cycles}
                onChange={(e) => setCustomPreset({...customPreset, cycles: Number(e.target.value)})}
                min="1"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-6 space-x-3">
          <button
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            onClick={() => setShowCustomForm(false)}
          >
            Cancel
          </button>
          
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            onClick={saveCustomPresetHandler}
          >
            Save Preset
          </button>
        </div>
      </div>
    </div>
  );

  const renderHistorySection = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Breathwork History</h3>
          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={handleToggleHistory}
          >
            <FaArrowLeft size={20} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Total Stats</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Sessions</span>
                <span className="font-medium">{stats.totalSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Completed</span>
                <span className="font-medium">{stats.completedSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Total Time</span>
                <span className="font-medium">{formatSessionTime(stats.totalTime)}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Last 30 Days</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Sessions</span>
                <span className="font-medium">{stats.recentSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Completed</span>
                <span className="font-medium">{stats.recentCompletedSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Total Time</span>
                <span className="font-medium">{formatSessionTime(stats.recentTotalTime)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <h4 className="font-semibold text-gray-800 mb-3">Recent Sessions</h4>
        
        {history.length > 0 ? (
          <div className="space-y-3">
            {history.map((session, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-800">{session.preset?.name || 'Custom Session'}</div>
                    <div className="text-sm text-gray-500">{formatDate(session.date)}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${session.completed ? 'text-green-600' : 'text-yellow-600'}`}>
                      {session.completed ? 'Completed' : 'Partial'}
                    </div>
                    <div className="text-sm text-gray-700">{formatSessionTime(session.duration)}</div>
                  </div>
                </div>
                
                {session.preset && (
                  <div className="mt-2 text-xs text-gray-500">
                    {session.preset.inhale}s in • {session.preset.hold1}s hold • 
                    {session.preset.exhale}s out • {session.preset.hold2}s hold • 
                    {session.preset.cycles} cycles
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            No history available yet
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <GuideModal guideKey="Breathwork" />
      <div className="py-6 sm:py-8">
      <div className="desktop-standard-container max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Breathwork</h2>
          
          <button
            className="flex items-center text-blue-600 hover:text-blue-800"
            onClick={handleToggleHistory}
          >
            <FaChartLine className="mr-1" /> History
          </button>
        </div>
        
        <p className="mt-2 text-gray-600">
          Practice guided breathing techniques to reduce stress and improve focus
        </p>
        
        {showSoundSettings && renderSoundSettings()}
        
        {currentCycle > 1 && totalCycles > 0 && renderProgressBar()}
        
        {renderBreathingCircle()}
        
        {renderControls()}
        
        {currentPhase === 'ready' && !isComplete && renderPresetSelector()}
      </div>
      
      {/* Modals */}
      {showCustomForm && renderCustomPresetForm()}
      {showHistory && renderHistorySection()}
      
      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes circle-expand {
            from { transform: scale(0.5); }
            to { transform: scale(1); }
          }
          
          @keyframes circle-contract {
            from { transform: scale(1); }
            to { transform: scale(0.5); }
          }
          
          @keyframes circle-pulsate {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
        `
      }} />
    </div>
    </>
  );
};

export default Breathwork;